import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { applyDrillThrough, decodeFilter } from "@/lib/pipelineFilterEncoder";
import { Download, Filter, Grid2X2, List, Plus, X } from "lucide-react";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { cn } from "@/design-system/lib/utils";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import {
  ACTIVE_STAGES,
  DEAL_PERMISSIONS,
  STAGE_LABEL,
  SUB_STATES_BY_STAGE,
  SUB_STATE_LABEL,
  subStateLabelFor,
} from "@/types/deals";
import type {
  DealListItemDto,
  DealListQuery,
  DealPipelineStage,
  DealSubState,
  RealEstateIncluded,
} from "@/types/deals";
import { useDealsList } from "@/hooks/deals/useDeals";
import { UnifiedKanban } from "@/components/deals/KanbanBoard";
import { ageBadgeStatus, ageSeverity, dealLabel, formatCurrency, formatStageAge } from "@/components/deals/dealFormat";
import { Badge } from "@/design-system/components/ui";
import { NewDealWizard } from "@/components/deals/NewDealWizard";
import { DeclineModal } from "@/components/deals/DeclineModal";
import { WithdrawModal } from "@/components/deals/WithdrawModal";
import { ReassignModal } from "@/components/deals/ReassignModal";
import { ArchiveConfirmModal } from "@/components/deals/ArchiveRestoreModals";
import type { DealCardAction } from "@/components/deals/DealCard";
import { downloadCsv } from "@/components/deals/csv";

type ViewMode = "kanban" | "table";

export function PipelinePage() {
  // Deals breadcrumb links back to the pipeline; the redundant "Deal
  // Pipeline" leaf is dropped on this page.
  useBreadcrumbs([{ label: "Deals", href: "/deal-flow/pipeline" }]);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(DEAL_PERMISSIONS.Create);
  const canDecline = hasPermission(DEAL_PERMISSIONS.Decline);
  const canWithdraw = hasPermission(DEAL_PERMISSIONS.Withdraw);
  const canReassign = hasPermission(DEAL_PERMISSIONS.Reassign);
  const canArchive = hasPermission(DEAL_PERMISSIONS.Archive);

  const [view, setView] = useState<ViewMode>("kanban");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<DealListQuery>(() => {
    // Deal Pipeline spec §5.4 — persist the show-closed toggle in
    // localStorage so the operator's preference survives navigation.
    let includeClosed = false;
    let showDeclined = false;
    try {
      includeClosed = localStorage.getItem("pipeline.includeClosed") === "true";
      showDeclined = localStorage.getItem("pipeline.showDeclined") === "true";
    } catch {
      // SSR or restricted storage — fall through to the default.
    }
    return {
      pageSize: 200,
      includeArchived: false,
      includeClosed,
      // Deal Pipeline spec §4.1a — when off (default) the server hides only
      // Declined deals.
      status: showDeclined ? "all" : undefined,
    };
  });
  const [wizardOpen, setWizardOpen] = useState(false);

  // Deal Pipeline spec §5.4 — accept ?filter={base64-json} drill-throughs from
  // the metrics dashboard. The PipelineFilterCriteria contract is a
  // superset of what DealListQuery understands today; unsupported keys
  // (failureLane/failureCode/dqrBucket/dealSizeBand/cycleTimeBand/
  // cohortQuarter) are silently no-op'd here so future charts can
  // drill through without coordinating a Pipeline release. When a
  // failure-tracking server filter lands, those keys can be mapped to
  // backend query params in this effect.
  useEffect(() => {
    const encoded = searchParams.get("filter");
    if (!encoded) return;
    const decoded = decodeFilter(encoded);
    setFilters((prev) => applyDrillThrough(prev, decoded));
    // Consume the param so we don't re-apply on every URL change.
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("filter");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Action modals — kept at the page level so any card / row can pop them.
  const [declineFor, setDeclineFor] = useState<DealListItemDto | null>(null);
  const [withdrawFor, setWithdrawFor] = useState<DealListItemDto | null>(null);
  const [reassignFor, setReassignFor] = useState<DealListItemDto | null>(null);
  const [archiveFor, setArchiveFor] = useState<DealListItemDto | null>(null);

  const cardActions: DealCardAction[] = [
    {
      key: "decline",
      label: "Decline…",
      onClick: (d) => setDeclineFor(d),
      hidden: !canDecline,
      destructive: true,
    },
    {
      key: "withdraw",
      label: "Withdraw…",
      onClick: (d) => setWithdrawFor(d),
      hidden: !canWithdraw,
      destructive: true,
    },
    {
      key: "reassign",
      label: "Reassign B1…",
      onClick: (d) => setReassignFor(d),
      hidden: !canReassign,
    },
    {
      key: "archive",
      label: "Archive…",
      onClick: (d) => setArchiveFor(d),
      hidden: !canArchive,
      destructive: true,
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Flow"
        title="Pipeline"
        actions={
          <div className="flex items-center gap-space-3">
            <ViewToggle value={view} onChange={setView} />
            <ExportButton query={filters} />
            {canCreate && (
              <Button size="sm" onClick={() => setWizardOpen(true)}>
                <Plus size={14} strokeWidth={1.5} aria-hidden /> New deal
              </Button>
            )}
          </div>
        }
      />

      <FilterBar value={filters} onChange={setFilters} />

      {view === "kanban" ? (
        <UnifiedKanban
          query={filters}
          cardActions={cardActions}
          stages={ACTIVE_STAGES}
          lookups={{ assigneeName: (deal) => deal.assignedC1DisplayName?.trim() || (deal.assignedC1Id ? "Needs assignment" : null) }}
          canCreate={canCreate}
          onCreateDeal={() => setWizardOpen(true)}
        />
      ) : (
        <DealTable query={filters} actions={cardActions} />
      )}

      <NewDealWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <DeclineModal
        open={!!declineFor}
        deal={declineFor}
        onClose={() => setDeclineFor(null)}
      />
      <WithdrawModal
        open={!!withdrawFor}
        deal={withdrawFor}
        onClose={() => setWithdrawFor(null)}
      />
      <ReassignModal
        open={!!reassignFor}
        deal={reassignFor}
        role="B1"
        onClose={() => setReassignFor(null)}
      />
      <ArchiveConfirmModal
        open={!!archiveFor}
        deal={archiveFor}
        onClose={() => setArchiveFor(null)}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Filter bar
// ----------------------------------------------------------------------

function FilterBar({
  value,
  onChange,
}: {
  value: DealListQuery;
  onChange: (next: DealListQuery) => void;
}) {
  const stage: DealPipelineStage[] = value.stage ?? [];
  const subState: DealSubState[] = value.subState ?? [];

  const allSubStates: DealSubState[] = useMemo(() => {
    if (stage.length === 0) {
      return ACTIVE_STAGES.flatMap((s) => SUB_STATES_BY_STAGE[s]);
    }
    return stage.flatMap((s) => SUB_STATES_BY_STAGE[s]);
  }, [stage]);

  const activeChips: { key: string; label: string; onRemove: () => void }[] = [];
  if (stage.length > 0) {
    activeChips.push({
      key: "stage",
      label: `Stage: ${stage.map((s) => STAGE_LABEL[s]).join(", ")}`,
      onRemove: () => onChange({ ...value, stage: undefined }),
    });
  }
  if (subState.length > 0) {
    activeChips.push({
      key: "substate",
      label: `Sub-state: ${subState.map((s) => SUB_STATE_LABEL[s]).join(", ")}`,
      onRemove: () => onChange({ ...value, subState: undefined }),
    });
  }
  if (value.hasOverdueDocs) {
    activeChips.push({
      key: "overdue",
      label: "Has overdue docs",
      onRemove: () => onChange({ ...value, hasOverdueDocs: undefined }),
    });
  }
  if (value.assignedTo) {
    activeChips.push({
      key: "assignedTo",
      label: `Assigned: ${value.assignedTo === "__me__" ? "Me" : value.assignedTo === "__unassigned__" ? "Unassigned" : value.assignedTo}`,
      onRemove: () => onChange({ ...value, assignedTo: undefined }),
    });
  }
  if (value.includeArchived) {
    activeChips.push({
      key: "archived",
      label: "Including archived",
      onRemove: () => onChange({ ...value, includeArchived: false }),
    });
  }
  if (value.realEstateIncluded) {
    const reLabel: Record<RealEstateIncluded, string> = {
      None: "Not included",
      Owned: "Included (owned)",
      Leased: "Included (leased)",
    };
    activeChips.push({
      key: "realEstate",
      label: `Real estate: ${reLabel[value.realEstateIncluded]}`,
      onRemove: () => onChange({ ...value, realEstateIncluded: undefined }),
    });
  }
  if (value.includeClosed) {
    activeChips.push({
      key: "closed",
      label: "Including closed",
      onRemove: () => {
        try {
          localStorage.setItem("pipeline.includeClosed", "false");
        } catch {
          /* ignore */
        }
        onChange({ ...value, includeClosed: false });
      },
    });
  }

  return (
    <Card>
      <div className="flex items-center gap-space-2 mb-space-3">
        <Filter size={14} className="text-gold-shadow" aria-hidden />
        <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          Filters
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-space-3">
        <FormField label="Search">
          <Input
            placeholder="Deal #, business, narrative…"
            value={value.q ?? ""}
            onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
          />
        </FormField>

        <UserFilter
          value={value.assignedTo ?? ""}
          onChange={(next) => onChange({ ...value, assignedTo: next || undefined })}
        />

        <FormField label="Stage">
          <Select
            value={stage[0] ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                stage: e.target.value ? [e.target.value as DealPipelineStage] : undefined,
                subState: undefined,
              })
            }
          >
            <option value="">All stages</option>
            {ACTIVE_STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Sub-state">
          <Select
            value={subState[0] ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                subState: e.target.value ? [e.target.value as DealSubState] : undefined,
              })
            }
            disabled={allSubStates.length === 0}
          >
            <option value="">All sub-states</option>
            {allSubStates.map((s) => (
              <option key={s} value={s}>
                {SUB_STATE_LABEL[s]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Status">
          <Select
            value={
              value.includeArchived ? "all" : value.hasOverdueDocs ? "overdue" : "active"
            }
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                ...value,
                hasOverdueDocs: v === "overdue" ? true : undefined,
                includeArchived: v === "all",
              });
            }}
          >
            <option value="active">Active deals only</option>
            <option value="overdue">Overdue documents</option>
            <option value="all">Include archived</option>
          </Select>
        </FormField>

        {/* Deal Pipeline spec §5.4 — RealEstateIncluded quick
            filter on the import-populated scalar. */}
        <FormField label="Real estate">
          <Select
            value={value.realEstateIncluded ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                realEstateIncluded: e.target.value
                  ? (e.target.value as RealEstateIncluded)
                  : undefined,
              })
            }
          >
            <option value="">Any</option>
            <option value="None">Not included</option>
            <option value="Owned">Included (owned)</option>
            <option value="Leased">Included (leased)</option>
          </Select>
        </FormField>
      </div>

      {/* Deal Pipeline spec §5.4 — Show-closed-deals toggle. */}
      <div className="mt-space-3 flex items-center gap-space-2">
        <label
          className="inline-flex items-center gap-space-2 text-small text-onyx cursor-pointer"
          data-testid="pipeline-include-closed-toggle"
        >
          <input
            type="checkbox"
            checked={!!value.includeClosed}
            onChange={(e) => {
              const next = e.target.checked;
              try {
                localStorage.setItem("pipeline.includeClosed", String(next));
              } catch {
                /* ignore */
              }
              onChange({ ...value, includeClosed: next });
            }}
          />
          Show closed deals
        </label>
        <span className="text-caption text-gray-mid">
          (default: hidden — deals converted to a Loan)
        </span>

        {/* Deal Pipeline spec §5.4 — Show-declined toggle. OFF (default)
            hides ratified-Declined deals; ON flips the ?status param to
            "all". DeclineProposed deals are always visible. */}
        <label
          className="inline-flex items-center gap-space-2 text-small text-onyx cursor-pointer ml-space-3"
          data-testid="pipeline-show-declined-toggle"
        >
          <input
            type="checkbox"
            checked={value.status === "all"}
            onChange={(e) => {
              const next = e.target.checked ? "all" : undefined;
              try {
                localStorage.setItem(
                  "pipeline.showDeclined",
                  String(e.target.checked),
                );
              } catch {
                /* ignore */
              }
              onChange({ ...value, status: next });
            }}
          />
          Show declined
        </label>
      </div>

      {activeChips.length > 0 && (
        <div className="mt-space-3 flex items-center gap-space-2 flex-wrap">
          {activeChips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-space-1 px-space-2 py-[3px] rounded-pill border border-gold/40 bg-gold/5 text-caption text-onyx"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                aria-label={`Remove ${c.label}`}
                className="hover:text-status-error"
              >
                <X size={12} aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------
// View toggle
// ----------------------------------------------------------------------

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const base =
    "h-8 px-space-3 inline-flex items-center gap-space-1 text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard";
  return (
    <div className="inline-flex rounded-md border border-border-light bg-white-pure overflow-hidden">
      <button
        type="button"
        onClick={() => onChange("kanban")}
        className={cn(
          base,
          value === "kanban"
            ? "bg-gold text-onyx hover:bg-gold-highlight"
            : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
        )}
        aria-pressed={value === "kanban"}
      >
        <Grid2X2 size={14} strokeWidth={1.5} aria-hidden /> Kanban
      </button>
      <button
        type="button"
        onClick={() => onChange("table")}
        className={cn(
          base,
          value === "table"
            ? "bg-gold text-onyx hover:bg-gold-highlight"
            : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
        )}
        aria-pressed={value === "table"}
      >
        <List size={14} strokeWidth={1.5} aria-hidden /> Table
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// Table view
// ----------------------------------------------------------------------

function DealTable({
  query,
  actions,
}: {
  query: DealListQuery;
  actions: DealCardAction[];
}) {
  const dealsQuery = useDealsList(query);
  const rows = dealsQuery.data?.data ?? [];

  // Deal Pipeline spec §5.4 — the import-populated
  // deal-structure scalars are opt-in columns; the table is already wide.
  const [showStructureCols, setShowStructureCols] = useState(false);

  const realEstateLabel: Record<RealEstateIncluded, string> = {
    None: "Not incl.",
    Owned: "Owned",
    Leased: "Leased",
  };

  const structureColumns: Column<DealListItemDto>[] = [
    {
      key: "cashAtClosing",
      header: "Cash @ close",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">
          {d.cashAtClosing != null ? formatCurrency(d.cashAtClosing) : "—"}
        </span>
      ),
    },
    {
      key: "subordinatedDebtAmount",
      header: "Sub. debt",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">
          {d.subordinatedDebtAmount != null ? formatCurrency(d.subordinatedDebtAmount) : "—"}
        </span>
      ),
    },
    {
      key: "realEstateIncluded",
      header: "Real estate",
      render: (d) => (
        <span className="text-small text-gray-mid">
          {d.realEstateIncluded ? realEstateLabel[d.realEstateIncluded] : "—"}
        </span>
      ),
    },
    {
      key: "collateralType",
      header: "Collateral",
      render: (d) => <span className="text-small text-gray-mid">{d.collateralType ?? "—"}</span>,
    },
  ];

  const columns: Column<DealListItemDto>[] = [
    {
      key: "dealNumber",
      header: "Deal #",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
        >
          {d.dealNumber}
        </Link>
      ),
    },
    {
      key: "businessName",
      header: "Business",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-semibold hover:text-gold-shadow"
        >
          {dealLabel(d)}
        </Link>
      ),
    },
    {
      key: "liaison",
      header: "Broker Liaison",
      render: (d) => (
        <span className={d.assignedC1Id ? "text-small text-onyx" : "text-caption text-gray-mid italic"}>
          {d.assignedC1Id ? d.assignedC1DisplayName?.trim() || "Needs assignment" : "Unassigned"}
        </span>
      ),
    },
    {
      key: "staging",
      header: "Staging",
      render: (d) => d.stagingSubmissionId ? (
        <Link
          to={`/deal-flow/deal-staging/${d.stagingSubmissionId}`}
          className="text-caption text-gold-shadow hover:underline"
        >
          View staging
        </Link>
      ) : (
        <span className="text-caption text-gray-mid">—</span>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      render: (d) => <span className="text-small">{STAGE_LABEL[d.stage]}</span>,
    },
    {
      key: "subState",
      header: "Sub-state",
      render: (d) => (
        <span className="text-small text-gray-mid">{subStateLabelFor(d.stage, d.subState) ?? "—"}</span>
      ),
    },
    {
      key: "purchasePrice",
      header: "Price",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">{formatCurrency(d.purchasePrice)}</span>
      ),
    },
    ...(showStructureCols ? structureColumns : []),
    {
      key: "age",
      header: "Days",
      render: (d) => {
        const sev = ageSeverity(d);
        return <Badge status={ageBadgeStatus(sev)}>{formatStageAge(d.stageEnteredDate)}</Badge>;
      },
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (d) => (
        <RowActionMenu actions={actions.filter((a) => !a.hidden)} deal={d} />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-2">
      <label className="self-end inline-flex items-center gap-space-2 text-caption text-gray-mid cursor-pointer">
        <input
          type="checkbox"
          checked={showStructureCols}
          onChange={(e) => setShowStructureCols(e.target.checked)}
        />
        Show deal-structure columns
      </label>
      <DataTable
        columns={columns}
        rows={rows}
        loading={dealsQuery.isLoading}
        error={dealsQuery.isError ? "Could not load deals." : null}
        rowKey={(d) => d.id}
        emptyMessage="No deals match your current filters."
      />
    </div>
  );
}

function RowActionMenu({ actions, deal }: { actions: DealCardAction[]; deal: DealListItemDto }) {
  const [open, setOpen] = useState(false);
  if (actions.length === 0) return null;
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-space-2 py-[2px] text-caption text-gray-mid hover:text-onyx"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Actions ▾
      </button>
      {open && (
        <div
          role="menu"
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 top-full z-10 min-w-[160px] bg-white-pure border border-border-light rounded-md shadow-lg py-space-1"
        >
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                a.onClick(deal);
              }}
              className={
                "w-full text-left px-space-3 py-space-1 text-small hover:bg-white-warm " +
                (a.destructive ? "text-status-error" : "text-onyx")
              }
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Deal Pipeline spec §5.4 — User filter that returns deals where the
// chosen user is assigned to ANY of the 7 pills (C1/B1/A1/A2/A3/A4/B2).
// Backend extension to honour `assignedTo` lands separately; until then
// the param is forwarded to the existing /api/deals endpoint where it's
// silently ignored. See DealListQuery.assignedTo doc comment.
function UserFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { user } = useAuth();
  // Cast a wide net — every role that can hold a pill.
  const usersQuery = useAssignableUsers("C1,B1,A1,A2,A3,A4,B2");

  return (
    <FormField label="Assigned to">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All users</option>
        {user?.id && (
          <option value={user.id}>Me ({user.displayName ?? "me"})</option>
        )}
        <option value="__unassigned__">Unassigned</option>
        <optgroup label="Specific user">
          {(usersQuery.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </optgroup>
      </Select>
    </FormField>
  );
}

function ExportButton({ query }: { query: DealListQuery }) {
  const dealsQuery = useDealsList(query);
  const rows = dealsQuery.data?.data ?? [];

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => downloadCsv("deals.csv", rows)}
      disabled={rows.length === 0}
      title="Export CSV"
      aria-label="Export deals as CSV"
    >
      <Download size={14} strokeWidth={1.5} aria-hidden />
    </Button>
  );
}
